import { PaginationParams } from '@/lib/api/client';

/**
 * Interface for takeoff filter parameters
 */
export interface TakeoffFilters {
  search?: string;
}

/**
 * Query key factory for takeoffs
 */
export const takeoffsKeys = {
  all: ['takeoffs'] as const,
  lists: (projectId: number) => [...takeoffsKeys.all, 'list', projectId] as const,
  list: (projectId: number, filters?: TakeoffFilters) =>
    [...takeoffsKeys.lists(projectId), { filters }] as const,
  details: () => [...takeoffsKeys.all, 'detail'] as const,
  detail: (id: number) => [...takeoffsKeys.details(), id] as const,
  itemMetadata: (takeoffId: number, itemId: number) =>
    [...takeoffsKeys.detail(takeoffId), 'items', itemId, 'metadata'] as const,
  bidScopeAddendum: (takeoffId: number) =>
    [...takeoffsKeys.detail(takeoffId), 'bid-scope-addendum'] as const,
  vendorQuotes: (takeoffId: number) =>
    [...takeoffsKeys.detail(takeoffId), 'vendor-quotes'] as const,
  specMetadata: (takeoffId: number) =>
    [...takeoffsKeys.detail(takeoffId), 'spec-metadata'] as const,
  rfiDraft: (issueId: number) => [...takeoffsKeys.all, 'rfi-draft', issueId] as const,
} as const;

/**
 * Type for takeoffs query keys
 */
export type TakeoffsKeys = typeof takeoffsKeys;

/**
 * Query key factory for takeoff item components
 */
export const componentKeys = {
  all: ['components'] as const,
  project: (projectId: number) => [...componentKeys.all, projectId] as const,
  item: (projectId: number, takeoffId: number, itemId: number) =>
    [...componentKeys.all, projectId, takeoffId, itemId] as const,
} as const;

/**
 * Type for component query keys
 */
export type ComponentKeys = typeof componentKeys;

/**
 * Query key factory for material units
 */
export const materialUnitsKeys = {
  all: ['material-units'] as const,
  lists: () => [...materialUnitsKeys.all, 'list'] as const,
} as const;

export type ProjectFilters = {
  query?: string;
  created_by_me?: 'true';
  awaiting_outcome?: 'true';
  mine?: 'true';
  open_bids?: 'true';
} & PaginationParams;

export type CriticalFindingFilters = { mine?: 'true'; page_size: number };

export function makeProjectKeys(documentSetId?: number) {
  const projectsKeys = {
    all: ['projects'] as const,
    lists: () => [...projectsKeys.all, 'list'] as const,
    list: (filters?: ProjectFilters) => [...projectsKeys.lists(), { filters }] as const,
    metrics: (scope: 'team' | 'me') => [...projectsKeys.all, 'metrics', scope] as const,
    criticalFindings: (filters: CriticalFindingFilters) =>
      [...projectsKeys.all, 'critical-findings', { filters }] as const,
    details: () => [...projectsKeys.all, 'detail'] as const,
    detail: (id: number) =>
      [
        ...projectsKeys.details(),
        id,
        ...(documentSetId === undefined ? [] : ['document-set', documentSetId]),
      ] as const,
    socketConnected: (id: number) => [...projectsKeys.detail(id), 'socket-connected'] as const,
    walkthrough: (id: number) => [...projectsKeys.detail(id), 'walkthrough'] as const,
    focusedAgentProgress: (id: number, takeoffId: number) =>
      [...projectsKeys.detail(id), 'focused-agent-progress', takeoffId] as const,
    uploads: (id: number) => [...projectsKeys.detail(id), 'uploads'] as const,
    documentsProcessingProgress: (id: number) =>
      [...projectsKeys.detail(id), 'documents-processing-progress'] as const,
    corpus: (id: number) => [...projectsKeys.detail(id), 'extraction-corpus'] as const,
    documentsViewerRoot: (id: number) => [...projectsKeys.detail(id), 'documents-viewer'] as const,
    documentsViewerCatalog: (id: number, revisionId?: number) =>
      [...projectsKeys.documentsViewerRoot(id), 'catalog', revisionId] as const,
    documentsViewer: (
      id: number,
      sourceKind?: 'blueprint' | 'document',
      uploadId?: number,
      projectDocumentId?: number,
      pageNumber?: number,
      regionId?: number,
      regionKind?: 'note' | 'table' | 'diagram',
      includePageData?: boolean,
      followReplacements = false,
      revisionId?: number,
    ) =>
      [
        ...projectsKeys.documentsViewerRoot(id),
        sourceKind ?? 'default',
        uploadId ?? 'no-upload',
        projectDocumentId ?? 'no-document',
        pageNumber ?? 'first',
        regionId ?? 'all-regions',
        regionKind ?? 'any-kind',
        includePageData ? 'with-page-data' : 'base-page',
        followReplacements,
        revisionId,
      ] as const,
    documentsViewerThumbnails: (
      id: number,
      sourceKind: 'blueprint' | 'document',
      sourceId: number,
    ) => [...projectsKeys.documentsViewerRoot(id), 'thumbnails', sourceKind, sourceId] as const,
  } as const;

  return projectsKeys;
}
export const projectsKeys = makeProjectKeys();

export const currentUserKeys = {
  details: ['currentUser'] as const,
  teams: ['currentUser', 'teams'] as const,
} as const;

export const currentUserBillingKeys = {
  details: ['currentUserBilling'] as const,
  plans: ['currentUserBillingPlans'] as const,
} as const;

export const documentKeys = {
  all: ['documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...documentKeys.lists(), { filters }] as const,
  details: () => [...documentKeys.all, 'detail'] as const,
  detail: (id: string) => [...documentKeys.details(), id] as const,
  byProject: (projectId: string) => [...documentKeys.all, 'byProject', projectId] as const,
};

export const takeoffItemTagsKeys = {
  all: ['takeoff-item-tags'] as const,
  lists: () => [...takeoffItemTagsKeys.all, 'list'] as const,
} as const;

/**
 * Query key factory for assemblies
 */
export const assembliesKeys = {
  all: ['assemblies'] as const,
  lists: () => [...assembliesKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...assembliesKeys.lists(), { filters }] as const,
  details: () => [...assembliesKeys.all, 'detail'] as const,
  detail: (id: string) => [...assembliesKeys.details(), id] as const,
  components: (id: string) => [...assembliesKeys.detail(id), 'components'] as const,
  aliases: (id: string) => [...assembliesKeys.detail(id), 'aliases'] as const,
} as const;

/**
 * Type for assemblies query keys
 */
export type AssembliesKeys = typeof assembliesKeys;

export const orgInvitesKeys = {
  all: ['org-invites'] as const,
  list: (orgId: number) => [...orgInvitesKeys.all, 'list', orgId] as const,
} as const;

export const orgMembersKeys = {
  all: ['org-members'] as const,
  list: (orgId: number) => [...orgMembersKeys.all, 'list', orgId] as const,
} as const;

export const myInvitesKeys = {
  all: ['my-invites'] as const,
} as const;

export const inviteKeys = {
  byToken: (token: string) => ['invite', token] as const,
} as const;

export const integrationsKeys = {
  all: ['integrations'] as const,
  accubidStatus: ['integrations', 'accubid', 'status'] as const,
  directorySyncStatus: (organizationId: number | undefined) =>
    ['integrations', 'directory-sync', 'status', organizationId] as const,
  accubidDatabases: ['integrations', 'accubid', 'databases'] as const,
  accubidProjects: (databaseToken: string) =>
    ['integrations', 'accubid', 'projects', databaseToken] as const,
  accubidEstimates: (databaseToken: string, projectId: string) =>
    ['integrations', 'accubid', 'estimates', databaseToken, projectId] as const,
  accubidCloseoutPreview: (databaseToken: string, estimateId: string) =>
    ['integrations', 'accubid', 'closeout-preview', databaseToken, estimateId] as const,
} as const;

export const metadataKeys = {
  all: ['metadata'] as const,
} as const;

/**
 * Query key factory for product catalogs
 */
export const productCatalogsKeys = {
  all: ['product-catalogs'] as const,
  lists: (organizationId: number, includeGlobal: boolean = false) =>
    [...productCatalogsKeys.all, 'list', organizationId, { includeGlobal }] as const,
  details: () => [...productCatalogsKeys.all, 'detail'] as const,
  detail: (organizationId: number, catalogId: number) =>
    [...productCatalogsKeys.details(), organizationId, catalogId] as const,
} as const;

/**
 * Type for product catalogs query keys
 */
export type ProductCatalogsKeys = typeof productCatalogsKeys;
